import {
  getOrganizationMemberListUrl,
  getOrganizationMemberUrl,
  getQueryKey,
  queryClient,
  stringCompare,
  useDeleteQuery,
  useGetQuery,
  useMe,
  usePatchQuery,
  usePostQuery,
  useRouteParams
} from "@quortex/front-common"
import { type QueryFunctionContext, useSuspenseQuery } from "@tanstack/react-query"
import type { OrgParams, PoolParams } from "./types"

//--------------------------------------------------------------------------------------------------
// URLs
//--------------------------------------------------------------------------------------------------

/** @returns /pools/?org=${orgUuid} */
export const getPoolListUrl = ({ orgUuid }: Required<OrgParams>) =>
  `/pools/?embed=()&org=${orgUuid}`

/** @returns /pools/${poolUuid}/?org=${orgUuid} */
export const getPoolUrl = ({ orgUuid, poolUuid }: Required<PoolParams>) =>
  `/pools/${poolUuid}/?embed=()&org=${orgUuid}`

/** @returns /pools/${poolUuid}/status/?org=${orgUuid} */
export const getPoolStatusDetailUrl = ({ orgUuid, poolUuid }: Required<PoolParams>) =>
  `/pools/${poolUuid}/status/?org=${orgUuid}`

//--------------------------------------------------------------------------------------------------
// Queries & mutations
//--------------------------------------------------------------------------------------------------

export const usePoolList = (params?: OrgParams) => {
  const mergedParams = useRouteParams(params)
  const listUrl = getPoolListUrl({ ...mergedParams })

  const queryKey = getQueryKey("ott", listUrl)

  const { queryFn } = useGetQuery<api.ott.PaginatedPoolList>("ott", listUrl)

  const sortedQueryFn = async ({ signal }: QueryFunctionContext) => {
    const data = await queryFn({ meta: undefined, queryKey, signal })

    return {
      ...data,
      results: data.results
        ? [...data.results].sort((a, b) => stringCompare(a.name, b.name))
        : data.results
    }
  }

  return { queryFn: sortedQueryFn, queryKey } as const
}

export const usePool = (params?: PoolParams) => {
  const mergedParams = useRouteParams(params)
  const url = getPoolUrl({ ...mergedParams })

  return useGetQuery<api.ott.Pool>("ott", url)
}

export const usePoolStatusDetail = (params?: PoolParams) => {
  const mergedParams = useRouteParams(params)
  const url = getPoolStatusDetailUrl(mergedParams)

  return useGetQuery<api.ott.PoolStatusDetail>("ott", url)
}

export const useAddPool = (params?: OrgParams) => {
  const mergedParams = useRouteParams(params)
  const listUrl = getPoolListUrl({ ...mergedParams })
  const { data: me } = useSuspenseQuery(useMe())
  const organizationMemberListUrl = getOrganizationMemberListUrl(mergedParams)
  const organizationMemberUrl = getOrganizationMemberUrl({ ...mergedParams, userUuid: me.uuid })

  type TRequest = DeepPartial<api.ott.Pool>
  type TResponse = api.ott.Pool

  return usePostQuery<TRequest, TResponse>("ott", listUrl, [listUrl, organizationMemberListUrl], {
    onSuccess: async () => {
      // Clear the organization member is required when we create a pool to get updated access
      // rights for the new pool.
      // NB: the organization members list lives on the USER API, not the OTT API
      await queryClient.refetchQueries({ queryKey: getQueryKey("user", organizationMemberUrl) })
    }
  })
}

export const useUpdatePool = (params?: PoolParams) => {
  const mergedParams = useRouteParams(params)
  const url = getPoolUrl({ ...mergedParams })
  const listUrl = getPoolListUrl({ ...mergedParams })

  type TRequest = DeepPartial<api.ott.Pool>
  type TResponse = api.ott.Pool

  return usePatchQuery<TRequest, TResponse>("ott", url, [listUrl])
}

export const useDeletePool = (params?: PoolParams) => {
  const mergedParams = useRouteParams(params)
  const url = getPoolUrl({ ...mergedParams })
  const listUrl = getPoolListUrl({ ...mergedParams })

  const confirmArg = `pool_uuid=${mergedParams.poolUuid}`
  return useDeleteQuery("ott", url, [listUrl], { confirmArg })
}
